/**
 * Nikola Tesla — „Unmatched Adventures: Tales to Amaze".
 *
 * ELECTRICAL OVERFLOW: dve cievky, nabité sú verejné počítadlo `coils`
 * (0–2, `counter` / `addCounter`). Hra začína s 1, na konci ťahu +1.
 * Na začiatku ťahu s oboma nabitými zasiahne susedov a odsunie ich o 1.
 *
 * „You may discharge coils: 1 Coil … / 2 Coils …" je voľba `option` —
 * „Also" pri dvoch cievkach pridáva k efektu jednej, „Instead" ho nahrádza.
 */

import { neighbors, shareZone } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, cardDef, dealDamage, discardRandom, drawCard, emit, gainAction, getMap, getPlayer, healFighter, millDeck } from '../ops.js';
import { areEnemies } from '../players.js';
import type { FighterId, PlayerIndex } from '../types.js';
import { addCounter, askMove, canMove, cancel as cancelSide, counter, fighterOrNull, opponentOf } from './helpers.js';
import type { CardEffect, EffectCtx, EffectRegistry, HeroSpecial } from './types.js';

const TESLA = 'nikola-tesla';
const COILS = 'coils';
const HERTZ = 'nikola-tesla.7-hertz';
const AC = 'nikola-tesla.the-alternating-current';
const DEATH_RAY = 'nikola-tesla.death-ray';
const INTENSE = 'nikola-tesla.intense-experimentation';
const STORM = 'nikola-tesla.lightning-storm';
const POLYPHASE = 'nikola-tesla.polyphase-coils';
const REPULSION = 'nikola-tesla.repulsion-blast';
const XRAY = 'nikola-tesla.x-ray-radiation';

const teslaOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === TESLA) ?? null;

const coils = (ctx: Ctx, player: PlayerIndex) => counter(ctx, player, COILS);
const charge = (ctx: Ctx, player: PlayerIndex, n: number) => addCounter(ctx, player, COILS, n, 2);

/**
 * „You may discharge coils" — voľba 1 alebo 2 cievok (len toľko, koľko je
 * nabitých). Preskočenie = nevybíja sa. `data` sa doručí ako `<n>:<data>`.
 */
function askDischarge(e: EffectCtx, effect: string, labels: [string, string], data = '', onSkip?: string): void {
  const have = coils(e.ctx, e.player);
  const skip = onSkip === undefined ? {} : { onSkip: { kind: 'effect' as const, effect, data: `0:${onSkip}` } };
  if (have === 0) {
    if (onSkip !== undefined) askChoice(e.ctx, { kind: 'auto', player: e.player, source: e.cardId, label: 'Tesla', optional: false, follow: skip.onSkip! });
    return;
  }
  const options = [1, 2]
    .filter((n) => n <= have)
    .map((n) => ({ label: `Vybiť ${n} ${n === 1 ? 'cievku' : 'cievky'}: ${labels[n - 1]}`, follow: { kind: 'effect' as const, effect, data: `${n}:${data}` } }));
  askChoice(e.ctx, {
    kind: 'option',
    player: e.player,
    source: e.cardId,
    label: 'Tesla: smieš vybiť cievky',
    optional: true,
    options,
    ...skip,
  });
}

/** Vybitie `n` cievok — vráti, koľko sa naozaj vybilo (medzitým sa mohli vybiť inak). */
function discharge(ctx: Ctx, player: PlayerIndex, n: number): number {
  return -addCounter(ctx, player, COILS, -n, 2);
}

const parse = (data: string): [number, string] => {
  const [n, ...rest] = data.split(':');
  return [Number(n), rest.join(':')];
};

/** „You may discharge coils: 1 Coil: Gain 1 action. 2 Coils: Also, draw 1 card." */
const sevenHertz: CardEffect = {
  after: (e) => askDischarge(e, HERTZ, ['získaj akciu', 'akcia a karta']),
  onFollow: (ctx, choice, data) => {
    const n = discharge(ctx, choice.player, parse(data)[0]);
    if (n >= 1) gainAction(ctx, choice.player);
    if (n >= 2) drawCard(ctx, choice.player);
  },
};

/** „Choose one: Charge both coils / Discharge both coils to have Tesla recover 2 health." */
const alternatingCurrent: CardEffect = {
  after: (e) => {
    const follow = (data: string) => ({ kind: 'effect' as const, effect: AC, data });
    askChoice(e.ctx, {
      kind: 'option',
      player: e.player,
      source: e.cardId,
      label: 'The Alternating Current: vyber jedno',
      optional: false,
      options: [
        { label: 'Nabiť obe cievky', follow: follow('charge') },
        ...(coils(e.ctx, e.player) === 2 ? [{ label: 'Vybiť obe — Tesla si vylieči 2', follow: follow('heal') }] : []),
      ],
    });
  },
  onFollow: (ctx, choice, data) => {
    if (data === 'charge') {
      charge(ctx, choice.player, 2);
      return;
    }
    const tesla = teslaOf(ctx, choice.player);
    if (discharge(ctx, choice.player, 2) === 2 && tesla) healFighter(ctx, tesla.id, 2);
  },
};

const side = (e: EffectCtx) => (e.role === 'defender' ? 'defender' : 'attacker');

/** „You may discharge coils: 1 Coil: This card's value is 5. 2 Coils: Instead, this card's value is 7." */
const deathRay: CardEffect = {
  duringChoice: (e) => askDischarge(e, DEATH_RAY, ['hodnota 5', 'hodnota 7'], side(e)),
  onFollow: (ctx, choice, data) => {
    const [n, s] = parse(data);
    const done = discharge(ctx, choice.player, n);
    const combat = ctx.state.combat;
    if (done > 0 && combat) (combat.flags ??= []).push(`deathRay:${s}:${done}`);
  },
  during: (e, value) => {
    const flag = e.ctx.state.combat?.flags?.find((f) => f.startsWith(`deathRay:${side(e)}:`));
    if (!flag) return value;
    return flag.endsWith(':2') ? 7 : 5;
  },
};

/** „Charge both coils. Gain 1 action." */
const fullyCharged: CardEffect = {
  scheme: (e) => {
    charge(e.ctx, e.player, 2);
    gainAction(e.ctx, e.player);
  },
};

/**
 * „Draw 1 card. You may discharge coils: 1 Coil: Instead, draw 2 cards.
 * 2 Coils: Instead, draw 3 cards and Tesla recovers 1 health."
 */
const intenseExperimentation: CardEffect = {
  after: (e) => askDischarge(e, INTENSE, ['2 karty namiesto 1', '3 karty a 1 zdravie'], '', ''),
  onFollow: (ctx, choice, data) => {
    const n = discharge(ctx, choice.player, parse(data)[0]);
    for (let i = 0; i < 1 + n; i++) drawCard(ctx, choice.player);
    const tesla = teslaOf(ctx, choice.player);
    if (n === 2 && tesla) healFighter(ctx, tesla.id, 1);
  },
};

/** „Charge one coil. If you won the combat, charge both." */
const kineticInduction: CardEffect = {
  after: (e) => void charge(e.ctx, e.player, e.won ? 2 : 1),
};

/** „You may discharge coils: 1 Coil: Deal 1 damage to each opposing fighter in your zone. 2 Coils: Instead, deal 2 damage." */
const lightningStorm: CardEffect = {
  after: (e) => askDischarge(e, STORM, ['1 poškodenie súperom v zóne', '2 poškodenia súperom v zóne']),
  onFollow: (ctx, choice, data) => {
    const n = discharge(ctx, choice.player, parse(data)[0]);
    const tesla = teslaOf(ctx, choice.player);
    if (!n || !tesla) return;
    const map = getMap(ctx);
    const targets = ctx.state.fighters
      .filter((f) => areEnemies(ctx.state, f.owner, choice.player) && shareZone(map, f.spaceId, tesla.spaceId))
      .map((f) => f.id);
    for (const id of targets) dealDamage(ctx, id, n);
  },
};

/**
 * „IMMEDIATELY: You may discharge coils: 1 Coil: Cancel all effects on your
 * opponent's card. 2 Coils: Also, ignore that card's value."
 */
const polyphaseCoils: CardEffect = {
  immediate: (e) => askDischarge(e, POLYPHASE, ['zruš efekty súperovej karty', 'zruš efekty aj hodnotu'], e.role === 'defender' ? 'attacker' : 'defender'),
  onFollow: (ctx, choice, data) => {
    const [n, theirs] = parse(data);
    const done = discharge(ctx, choice.player, n);
    const combat = ctx.state.combat;
    if (!done || !combat) return;
    cancelSide(ctx, theirs as 'attacker' | 'defender');
    if (done === 2) (combat.flags ??= []).push(theirs === 'attacker' ? 'zeroAttack' : 'zeroDefense');
  },
};

/** „Move all opposing fighters up to 2 spaces. Gain 1 action." */
const remoteControl: CardEffect = {
  scheme: (e) => {
    const enemies = e.ctx.state.fighters.filter((f) => areEnemies(e.ctx.state, f.owner, e.player));
    for (const f of enemies) {
      if (!canMove(e.ctx, f.id, 2, false)) continue;
      askChoice(e.ctx, {
        kind: 'move',
        player: e.player,
        source: e.cardId,
        label: `Remote Control: presuň ${f.name} až o 2`,
        optional: true,
        fighters: [f.id],
        max: 2,
        through: false,
      });
    }
    gainAction(e.ctx, e.player);
  },
};

/**
 * „Move the opposing fighter up to 2 spaces. You may discharge coils:
 * 1 Coil: Also, move Tesla up to 2 spaces. 2 Coils: Also, your opponent
 * discards 1 random card."
 */
const repulsionBlast: CardEffect = {
  after: (e) => {
    if (e.opponentFighterId) askMove(e, { fighters: [e.opponentFighterId], max: 2, label: 'Repulsion Blast: presuň súpera až o 2' });
    askDischarge(e, REPULSION, ['aj Tesla sa presunie o 2', 'presun Teslu a súper odhodí náhodnú kartu'], String(opponentOf(e) ?? ''));
  },
  onFollow: (ctx, choice, data) => {
    const [n, opponent] = parse(data);
    const done = discharge(ctx, choice.player, n);
    const tesla = teslaOf(ctx, choice.player);
    if (done >= 1 && tesla && canMove(ctx, tesla.id, 2, false)) {
      askChoice(ctx, {
        kind: 'move',
        player: choice.player,
        source: choice.source,
        label: 'Repulsion Blast: presuň Teslu až o 2',
        optional: true,
        fighters: [tesla.id],
        max: 2,
        through: false,
      });
    }
    if (done >= 2 && opponent !== '') discardRandom(ctx, Number(opponent), true);
  },
};

/**
 * „DURING: Reveal the top card of your opponent's deck. You may discharge
 * coils: 1 Coil: Discard that card. 2 Coils: Also, add its boost value to
 * this card's value." Vrchná karta sa odhalí všetkým.
 */
const xRayRadiation: CardEffect = {
  duringChoice: (e) => {
    const opponent = opponentOf(e);
    const top = opponent === null ? undefined : getPlayer(e.ctx.state, opponent).deck[0];
    if (opponent === null || !top) return;
    emit(e.ctx, { type: 'cardRevealed', player: opponent, cardId: top });
    askDischarge(e, XRAY, ['odhoď ju', 'odhoď a pripočítaj jej boost'], `${opponent}:${top}:${side(e)}`);
  },
  onFollow: (ctx, choice, data) => {
    const [n, rest] = parse(data);
    const [opponent, top, s] = rest.split(':') as [string, string, 'attacker' | 'defender'];
    const done = discharge(ctx, choice.player, n);
    const deck = getPlayer(ctx.state, Number(opponent)).deck;
    if (!done || deck[0] !== top) return;
    millDeck(ctx, Number(opponent), 1);
    const combat = ctx.state.combat;
    if (done === 2 && combat) {
      combat.boosts ??= { attacker: 0, defender: 0 };
      combat.boosts[s] += cardDef(ctx, top).boost;
    }
  },
};

export const teslaCards: EffectRegistry = {
  [HERTZ]: sevenHertz,
  [AC]: alternatingCurrent,
  [DEATH_RAY]: deathRay,
  'nikola-tesla.fully-charged': fullyCharged,
  [INTENSE]: intenseExperimentation,
  'nikola-tesla.kinetic-induction': kineticInduction,
  [STORM]: lightningStorm,
  [POLYPHASE]: polyphaseCoils,
  'nikola-tesla.remote-control': remoteControl,
  [REPULSION]: repulsionBlast,
  [XRAY]: xRayRadiation,
};

export const teslaSpecial: HeroSpecial = {
  name: 'Electrical Overflow',
  /** „Start the game with 1 coil charged." */
  setup: (ctx, player) => {
    const seat = ctx.state.players[player];
    if (seat) (seat.counters ??= {})[COILS] = 1;
  },
  /** „At the end of your turn, charge 1 coil." */
  endOfTurn: (ctx, player) => void charge(ctx, player, 1),
  /**
   * „At the start of your turn, if both coils are charged, deal 1 damage to
   * each opposing fighter adjacent to Tesla and move them up to 1 space."
   */
  startOfTurn: (ctx, player) => {
    const tesla = teslaOf(ctx, player);
    if (!tesla || coils(ctx, player) < 2) return;
    const around = new Set(neighbors(getMap(ctx), tesla.spaceId));
    const hit: FighterId[] = ctx.state.fighters
      .filter((f) => areEnemies(ctx.state, f.owner, player) && around.has(f.spaceId))
      .map((f) => f.id);
    if (hit.length === 0) return;
    emit(ctx, { type: 'specialUsed', player, heroId: TESLA, name: 'Electrical Overflow: výboj do susedov' });
    for (const id of hit) dealDamage(ctx, id, 1);
    for (const id of hit) {
      const f = fighterOrNull(ctx, id);
      if (!f || !canMove(ctx, id, 1, false)) continue;
      askChoice(ctx, {
        kind: 'move',
        player,
        source: null,
        label: `Electrical Overflow: smieš odsunúť ${f.name} o 1`,
        optional: true,
        fighters: [id],
        max: 1,
        through: false,
      });
    }
  },
};
